// src/pages/SurveyEdit.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SurveyCreator, SurveyCreatorComponent } from "survey-creator-react";
import { Serializer, Model, Question } from "survey-core";
import "survey-core/survey-core.css";
import "survey-creator-core/survey-creator-core.css";

import { Box, Typography } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

import { auth, db } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  type DocumentData,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  type UpdateData,
  updateDoc,
  where,
} from "firebase/firestore";

import { useSurveyAccess } from "../hooks/useSurveyAccess"; // centralized access helper
import { getAppVersion } from "../utils/version";
import { debug, info, warn, error } from "../logger";

let isFirestoreListPropertyRegistered = false;

type FirestoreListChoice = {
  value: string;
  text: string;
};

async function fetchAvailableLists(userId: string): Promise<FirestoreListChoice[]> {
  try {
    const userSnap = await getDoc(doc(db, "users", userId));
    const isAdmin = userSnap.exists() && (userSnap.data() as any)?.accountRole === "admin";
    const listsRef = collection(db, "lists");
    const listsQuery = isAdmin ? listsRef : query(listsRef, where("userId", "==", userId));
    const snapshot = await getDocs(listsQuery);

    const ownerIds = Array.from(
      new Set(
        snapshot.docs
          .map((listDoc) => (listDoc.data() as any)?.ownerId ?? (listDoc.data() as any)?.userId)
          .filter((ownerId): ownerId is string => typeof ownerId === "string" && ownerId !== userId)
      )
    );
    const ownerEmails = new Map<string, string>();
    if (isAdmin && ownerIds.length > 0) {
      await Promise.all(
        ownerIds.map(async (ownerId) => {
          const ownerSnap = await getDoc(doc(db, "users", ownerId));
          const ownerEmail = ownerSnap.exists() ? (ownerSnap.data() as any)?.email : undefined;
          ownerEmails.set(ownerId, ownerEmail || ownerId);
        })
      );
    }

    return snapshot.docs
      .filter((listDoc) => ((listDoc.data() as any)?.status ?? "active") !== "deleted")
      .map((listDoc) => {
        const data = listDoc.data() as any;
        const ownerId = data?.ownerId ?? data?.userId;
        const name = (data.name as string) || listDoc.id;
        const ownerEmail = typeof ownerId === "string" ? ownerEmails.get(ownerId) : undefined;
        return {
          value: listDoc.id,
          text: ownerEmail ? `${name} (${ownerEmail})` : name,
        };
      });
  } catch (e) {
    error("Error fetching available lists from Firestore:", e);
    throw e;
  }
}

function registerFirestoreListProperty(choices: FirestoreListChoice[]) {
  const existingProperty = Serializer.findProperty("dropdown", "firestoreListId");
  if (existingProperty) {
    existingProperty.type = "dropdown";
    existingProperty.category = "General";
    existingProperty.displayName = "Load from Firestore List";
    existingProperty.visibleIf = (obj: any) => obj.getType() === "dropdown";
    existingProperty.setChoices(choices);
    isFirestoreListPropertyRegistered = true;
    return;
  }

  if (!isFirestoreListPropertyRegistered) {
    Serializer.addProperty("dropdown", {
      name: "firestoreListId",
      category: "General",
      displayName: "Load from Firestore List",
      type: "dropdown",
      choices,
      visibleIf: (obj: any) => obj.getType() === "dropdown",
    });
    isFirestoreListPropertyRegistered = true;
    return;
  }
}

type SurveyStatus = "published" | "unpublished" | "deleted";

export default function Editor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Access: owner/editor/admin may edit
  const shouldSkipAccess = id === "new" || !id;
  const { loading: accessLoading, allowed, role, survey } = useSurveyAccess(
    shouldSkipAccess ? "" : (id as string),
    "edit"
  );

  const [creator, setCreator] = useState<SurveyCreator | null>(null);
  const creatorRef = useRef<SurveyCreator | null>(null);

  const [listsRegisteredForUid, setListsRegisteredForUid] = useState<string | null>(null);

  // Handle "new" route -> generate ID and redirect
  useEffect(() => {
    if (id === "new") {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }
      const newId = uuidv4();
      navigate(`/edit/${newId}`, { replace: true });
    }
  }, [id, navigate, role]);

  // Create SurveyCreator once
  useEffect(() => {
    const sc = new SurveyCreator({
      showLogicTab: true,
      isAutoSave: true,
    });
    creatorRef.current = sc;
    setCreator(sc);

    // Preview tab: hydrate Firestore-backed dropdowns + allow "Other" to be saved
    sc.onSurveyInstanceCreated.add((sender, options) => {
      if (options.reason === "preview" || options.reason === "test") {
        const previewModel = options.survey as Model;

        previewModel.onAfterRenderQuestion.add(
          async (_sender: Model, opts: { question: Question; htmlElement: HTMLElement }) => {
            if (opts.question.getType() === "dropdown") {
              const dropdownQuestion = opts.question;
              const firestoreListId = dropdownQuestion.getPropertyValue("firestoreListId");
              if (firestoreListId) {
                const itemsRef = collection(db, "lists", firestoreListId, "items");
                onSnapshot(
                  itemsRef,
                  (snapshot) => {
                    const fetchedChoices = snapshot.docs.map((d) => {
                      const data = d.data() as any;
                      return {
                        value: data.value || d.id,
                        text: data.name || data.value || d.id,
                      };
                    });
                    (dropdownQuestion as any).choices = fetchedChoices;
                  },
                  (err) => {
                    error(`[Editor Preview] onSnapshot error for list ${firestoreListId}:`, err);
                    (dropdownQuestion as any).choices = [];
                  }
                );
              }
              (dropdownQuestion as any).hasOther = true;
              (dropdownQuestion as any).otherText = "Other (describe)";
              (dropdownQuestion as any).otherPlaceholder = "Enter new item here";
            }
          }
        );

        previewModel.onComplete.add(async (modelSender: Model) => {
          const currentUser = auth.currentUser;
          if (!currentUser) return;

          const data = (modelSender as any).data;
          for (const qName in data) {
            const q = modelSender.getQuestionByName(qName);
            if (q?.getType() === "dropdown") {
              const listId = (q as any).getPropertyValue("firestoreListId");
              const selected = data[qName];
              const otherText = data[`${qName}-Comment`];
              if (listId && selected === "other" && otherText) {
                try {
                  await addDoc(collection(db, "lists", listId, "items"), {
                    name: otherText,
                    value: otherText,
                    createdAt: serverTimestamp(),
                    userId: currentUser.uid,
                  });
                } catch (e) {
                  error(`[Editor Preview] Failed to save “other” choice to list ${listId}:`, e);
                }
              }
            }
          }
          alert("Preview complete—check console for details.");
        });
      }
    });

    return () => {
      creatorRef.current?.dispose();
      creatorRef.current = null;
    };
  }, []);

  // Register dropdown -> firestoreListId choices once per logged-in user
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    if (listsRegisteredForUid === currentUser.uid) return;

    (async () => {
      try {
        const availableLists = await fetchAvailableLists(currentUser.uid);
        registerFirestoreListProperty(availableLists);
        setListsRegisteredForUid(currentUser.uid);
      } catch {
        // already logged
      }
    })();
  }, [listsRegisteredForUid]);

  // Load survey JSON when access grants and we have a doc
  useEffect(() => {
    if (!creatorRef.current) return;
    if (shouldSkipAccess) return; // handled by redirect earlier
    if (accessLoading) return;

    if (!allowed) {
      warn("[Editor] Access denied for edit; redirecting to /surveys");
      navigate("/surveys", { replace: true });
      return;
    }

    if (!survey) {
      warn("[Editor] No survey doc available after access check.");
      navigate("/surveys", { replace: true });
      return;
    }

    if (survey.status === "deleted") {
      warn("[Editor] Survey is deleted; redirecting.");
      navigate("/surveys", { replace: true });
      return;
    }

    try {
      creatorRef.current.JSON = survey.json || {};
    } catch (e) {
      error("[Editor] Failed to set creator JSON:", e);
    }
  }, [allowed, accessLoading, navigate, shouldSkipAccess, survey]);

  // Auto-save
  useEffect(() => {
    if (!creatorRef.current) return;
    const sc = creatorRef.current;

    sc.saveSurveyFunc = async (saveNo: number, callback: (success: boolean, errMsg?: string) => void) => {
      const currentUser = auth.currentUser;
      const surveyId = id as string;

      if (!currentUser) {
        callback(false, "Authentication required.");
        navigate("/login");
        return;
      }

      if (!surveyId) {
        callback(false, "No survey ID.");
        return;
      }

      const ref = doc(db, "surveys", surveyId);

      try {
        // Preserve existing status/legacy field
        const snapNow = await getDoc(ref);
        const existing = snapNow.exists() ? (snapNow.data() as any) : {};

        const currentStatus: SurveyStatus =
          typeof existing.status === "string"
            ? (existing.status as SurveyStatus)
            : existing.published === true
            ? "published"
            : "unpublished";

        const title =
          sc.JSON?.title && String(sc.JSON.title).trim() !== ""
            ? String(sc.JSON.title).trim()
            : "Untitled Survey";

        const payload: UpdateData<DocumentData> = {
          json: sc.JSON,
          title,
          status: currentStatus,
          published: currentStatus === "published", // legacy sync
          updatedAt: serverTimestamp(),
          // keep roles/memberIds as-is
        };

        const existingOwnerId = existing.ownerId ?? existing.userId;

        // Editors must preserve legacy ownership shape exactly. On some older shared
        // surveys there is no owner field at all, and trying to introduce one makes
        // the first save fail against Firestore rules.
        if (existing.userId != null && role !== "editor") {
          payload.userId = existing.userId;
        } else if (role !== "editor") {
          payload.userId = existingOwnerId || currentUser.uid;
        }

        if (existing.ownerId != null) {
          payload.ownerId = existing.ownerId;
        } else if (existing.userId != null && role !== "editor") {
          payload.ownerId = existing.userId;
        } else if (role !== "editor" && existingOwnerId != null) {
          payload.ownerId = existingOwnerId;
        }

        debug("[Editor] Save attempt", {
          version: getAppVersion(),
          surveyId,
          role,
          existingUserId: existing.userId ?? null,
          existingOwnerId: existing.ownerId ?? null,
          existingRoleKeys: Object.keys(existing.roles ?? {}),
          existingMemberIds: existing.memberIds ?? [],
          payloadKeys: Object.keys(payload),
          payloadUserId: (payload.userId as string | undefined) ?? null,
          payloadOwnerId: (payload.ownerId as string | undefined) ?? null,
        });

        if (snapNow.exists()) {
          await updateDoc(ref, payload);
        } else {
          await setDoc(ref, payload, { merge: true });
        }

        info("[Editor] Survey saved successfully.");
        callback(true);
      } catch (e: any) {
        error("[Editor] SAVE FAILED:", {
          version: getAppVersion(),
          code: e?.code || null,
          message: e?.message || String(e),
          role,
          surveyId,
        });
        callback(false, e?.message || "Save failed.");
      }
    };
  }, [id, navigate, role]);

  // ------- Render states -------
  if (id === "new") {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Preparing new survey…</Typography>
      </Box>
    );
  }

  if (!creator || accessLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading editor…</Typography>
      </Box>
    );
  }

  if (!allowed) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">You don’t have permission to edit this survey.</Typography>
      </Box>
    );
  }

  // ------- Main -------
  return (
    <Box sx={{ flexGrow: 1, width: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <SurveyCreatorComponent creator={creator} />
      </Box>

      {/* Subtle access indicator at the very bottom */}
      <Box sx={{ p: 1.5, textAlign: "right", color: "text.secondary", fontSize: 12 }}>
        Your access: {role}
      </Box>
    </Box>
  );
}
